
import React, { useState } from 'react';
import type { Theme } from '../App';
import { Loader2Icon, MoonIcon, SunIcon, ArrowLeftIcon, LockIcon, UnlockIcon } from '../constants';
import { login, loginWithToken, register, requestPasswordReset, resetPassword } from '../services/authService';
import type { User } from '../types';
import { BrandMark } from './Header';

interface AuthProps {
    onLoginSuccess: (user: User) => void;
    theme: Theme;
    onToggleTheme: () => void;
    showToast: (message: string, type?: 'success' | 'error') => void;
}

type View = 'login' | 'register' | 'forgot-password' | 'forgot-password-sent' | '2fa';

interface InputFieldProps {
    id: string;
    label: string;
    type: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    autoComplete?: string;
    autoFocus?: boolean;
    inputMode?: string;
    pattern?: string;
    maxLength?: number;
    className?: string;
}

const InputField: React.FC<InputFieldProps> = ({
    id, label, type, value, onChange, placeholder, autoComplete, autoFocus, inputMode, pattern, maxLength, className
}) => (
    <div>
        <label htmlFor={id} className="field-label">
            {label}
        </label>
        <input
            type={type}
            id={id}
            name={id}
            required
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            inputMode={inputMode as any}
            pattern={pattern}
            maxLength={maxLength}
            spellCheck="false"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`input !min-h-[44px] !text-base ${className || ''}`}
            placeholder={placeholder}
        />
    </div>
);

interface SubmitButtonProps {
    text: string;
    loadingText: string;
    isLoading: boolean;
}

const SubmitButton: React.FC<SubmitButtonProps> = ({ text, loadingText, isLoading }) => (
    <button type="submit" disabled={isLoading} className="btn btn-primary w-full !min-h-[44px] !text-[15px]">
        {isLoading ? (
            <>
                <Loader2Icon className="h-4 w-4 animate-spin" />
                <span>{loadingText}</span>
            </>
        ) : (
            text
        )}
    </button>
);

/** Kleiner Linienplan als Leitbild: drei Linien, je Art eine, mit echten Beispielstationen */
const RouteMap: React.FC = () => {
    const lines = [
        { label: 'Prompt', color: 'var(--kind-prompt)', stops: ['Blog-Einleitung', 'Meeting-Protokoll', 'Code-Review'] },
        { label: 'Skill', color: 'var(--kind-skill)', stops: ['code-audit', 'seo-optimizer', 'humanizer'] },
        { label: 'Workflow', color: 'var(--kind-workflow)', stops: ['Recherche', 'Entwurf', 'Prüfung', 'Freigabe'] },
    ];
    return (
        <div className="space-y-7" aria-hidden="true">
            {lines.map((line, li) => (
                <div key={line.label}>
                    <div className="kind-label mb-2" style={{ color: `rgb(${line.color})` }}>{line.label}</div>
                    <div className="relative flex items-center justify-between">
                        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[5px] rounded-full" style={{ background: `rgb(${line.color})` }} />
                        {line.stops.map((stop, si) => (
                            <div key={stop} className="relative flex flex-col items-center animate-station" style={{ animationDelay: `${(li * 4 + si) * 60}ms` }}>
                                <span className="block h-4 w-4 rounded-full bg-raised" style={{ border: `4px solid rgb(${line.color})` }} />
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between mt-2">
                        {line.stops.map((stop, si) => (
                            <span key={stop} className={`font-mono text-[11px] text-graphite ${si === 0 ? 'text-left' : si === line.stops.length - 1 ? 'text-right' : 'text-center'} w-0 whitespace-nowrap flex ${si === 0 ? 'justify-start' : si === line.stops.length - 1 ? 'justify-end' : 'justify-center'}`}>{stop}</span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

const Auth: React.FC<AuthProps> = ({ onLoginSuccess, theme, onToggleTheme, showToast }) => {
    const [view, setView] = useState<View>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [token, setToken] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Store email between password and subsequent steps (2FA, password reset)
    const [pendingEmail, setPendingEmail] = useState('');
    const [resetToken, setResetToken] = useState('');

    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        const result = await login(email, password);
        if (result.success) {
            if (result.twoFactorRequired) {
                setPendingEmail(email);
                setToken('');
                setView('2fa');
                showToast('Bitte geben Sie Ihren 2FA-Code ein.');
            } else if (result.user) {
                onLoginSuccess(result.user);
                showToast(result.message, 'success');
            }
        } else {
            showToast(result.message, 'error');
        }
        setIsLoading(false);
    };

    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            showToast("Passwörter stimmen nicht überein.", "error");
            return;
        }
        setIsLoading(true);
        const result = await register(email, password);
        if (result.success) {
            showToast(result.message, 'success');
            const loginResult = await login(email, password);
            if (loginResult.success && loginResult.user) {
                onLoginSuccess(loginResult.user);
            } else {
                 showToast(loginResult.message, 'error');
            }
        } else {
            showToast(result.message, 'error');
        }
        setIsLoading(false);
    }
    
    const handle2faSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        const result = await loginWithToken(pendingEmail, token);
        if (result.success && result.user) {
            onLoginSuccess(result.user);
            showToast(result.message, 'success');
        } else {
            showToast(result.message, 'error');
        }
        setIsLoading(false);
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        const result = await requestPasswordReset(email);
        showToast(result.message, result.success ? 'success' : 'error');
        if (result.success) {
            setPendingEmail(email);
            setResetToken(result.token || '');
            setView('forgot-password-sent');
        }
        setIsLoading(false);
    };
    
    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resetToken.trim()) {
            showToast("Bitte geben Sie das Sicherheitstoken ein.", "error");
            return;
        }
        if (password !== confirmPassword) {
            showToast("Passwörter stimmen nicht überein.", "error");
            return;
        }
        setIsLoading(true);
        const result = await resetPassword(pendingEmail, resetToken, password);
        showToast(result.message, result.success ? 'success' : 'error');
        if (result.success) {
            setView('login');
            setEmail(pendingEmail);
            setPassword('');
            setConfirmPassword('');
        }
        setIsLoading(false);
    };
    const renderContent = () => {
        switch(view) {
            case '2fa':
                return (
                     <form onSubmit={handle2faSubmit} className="space-y-6">
                         <button 
                            type="button" 
                            onClick={() => setView('login')} 
                            className="flex items-center text-sm text-graphite hover:text-ink"
                         >
                            <ArrowLeftIcon />
                            <span className="ml-2">Zurück zum Login</span>
                         </button>
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-lg bg-ink/[0.06] text-ink flex items-center justify-center mx-auto mb-4">
                                <LockIcon />
                            </div>
                            <h2 className="font-display text-2xl font-semibold text-ink mb-2">
                                Zwei-Faktor-Authentifizierung
                            </h2>
                            <p className="text-sm text-graphite mb-6">
                                Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.
                            </p>
                        </div>
                        <InputField
                            id="token"
                            label="Sicherheitscode"
                            type="text"
                            value={token}
                            onChange={setToken}
                            autoFocus
                            inputMode="numeric"
                            pattern="\d{6}"
                            maxLength={6}
                            placeholder="000000"
                            className="text-center text-xl sm:text-2xl tracking-widest h-14"
                        />
                        <SubmitButton text="Bestätigen" loadingText="Wird geprüft …" isLoading={isLoading} />
                    </form>
                );
            case 'forgot-password':
                return (
                     <form onSubmit={handleForgotPassword} className="space-y-6">
                        <button 
                            type="button" 
                            onClick={() => setView('login')} 
                            className="flex items-center text-sm text-graphite hover:text-ink"
                        >
                           <ArrowLeftIcon />
                           <span className="ml-2">Zurück zum Login</span>
                        </button>
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-lg bg-ink/[0.06] text-ink flex items-center justify-center mx-auto mb-4">
                                <LockIcon />
                            </div>
                            <h2 className="font-display text-2xl font-semibold text-ink mb-2">
                                Passwort vergessen?
                            </h2>
                            <p className="text-sm text-graphite mb-6">
                                Geben Sie Ihre E-Mail-Adresse ein. Danach legen Sie direkt ein neues Passwort fest.
                            </p>
                        </div>
                        <InputField id="email" label="E-Mail-Adresse" type="email" value={email} onChange={setEmail} autoFocus autoComplete="email" />
                        <SubmitButton text="Weiter" loadingText="Wird gesendet …" isLoading={isLoading} />
                    </form>
                );
            case 'forgot-password-sent':
                 return (
                     <form onSubmit={handleResetPassword} className="space-y-6">
                        <button 
                            type="button" 
                            onClick={() => setView('login')} 
                            className="flex items-center text-sm text-graphite hover:text-ink"
                        >
                           <ArrowLeftIcon />
                           <span className="ml-2">Zurück zum Login</span>
                        </button>
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-lg bg-ink/[0.06] text-ink flex items-center justify-center mx-auto mb-4">
                                <UnlockIcon />
                            </div>
                            <h2 className="font-display text-2xl font-semibold text-ink mb-2">
                                Neues Passwort festlegen
                            </h2>
                            <p className="text-sm text-graphite mb-6">
                                Erstellen Sie ein neues Passwort für <strong className="text-ink">{pendingEmail}</strong>.
                            </p>
                        </div>
                        <InputField id="reset-token" label="Sicherheitstoken" type="text" value={resetToken} onChange={setResetToken} placeholder="Sicherheitstoken eingeben" />
                        <InputField id="password" label="Neues Passwort" type="password" value={password} onChange={setPassword} autoComplete="new-password" />
                        <InputField id="confirm-password" label="Neues Passwort bestätigen" type="password" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
                        <SubmitButton text="Passwort speichern" loadingText="Wird gespeichert …" isLoading={isLoading} />
                    </form>
                 );
            default:
                return null;
        }
    };

    const isLoginOrRegister = view === 'login' || view === 'register';

    return (
        <div className="min-h-screen w-full flex flex-col lg:flex-row bg-paper font-sans">
            {/* Leitbild */}
            <section className="lg:w-[52%] bg-sheet border-b lg:border-b-0 lg:border-r border-rule flex flex-col px-6 sm:px-10 lg:px-14 py-6 lg:py-10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <BrandMark className="h-8 w-8" />
                        <span className="font-display text-lg font-semibold text-ink">Prompt Manager Pro</span>
                    </div>
                    <button onClick={onToggleTheme} className="icon-btn lg:hidden" aria-label={theme === 'dark' ? 'Helles Design einschalten' : 'Dunkles Design einschalten'}>
                        {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
                    </button>
                </div>

                <div className="hidden lg:flex flex-1 flex-col justify-center max-w-[520px] py-10">
                    <h1 className="font-display text-[44px] leading-[1.05] font-semibold text-ink tracking-[-0.015em]">
                        Prompts, Skills und Workflows. Eine Bibliothek.
                    </h1>
                    <p className="mt-4 text-[17px] text-graphite leading-relaxed max-w-[44ch]">
                        Legen Sie Anweisungen für KI-Assistenten an einem Ort ab, ordnen Sie sie in Ordnern und kopieren Sie sie mit einem Klick, als Text, als fertige SKILL.md oder als Ablauf in Schritten.
                    </p>
                    <div className="mt-12">
                        <RouteMap />
                    </div>
                </div>

                <p className="hidden lg:block text-xs text-graphite">© {new Date().getFullYear()} Prompt Manager Pro</p>
            </section>

            {/* Formular */}
            <main className="flex-1 flex flex-col">
                <div className="hidden lg:flex justify-end p-6">
                    <button onClick={onToggleTheme} className="icon-btn" aria-label={theme === 'dark' ? 'Helles Design einschalten' : 'Dunkles Design einschalten'}>
                        {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
                    </button>
                </div>
                <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-10 lg:py-0">
                    <div className="w-full max-w-[400px]">
                        {isLoginOrRegister ? (
                            <>
                                <h2 className="font-display text-[28px] font-semibold text-ink leading-tight">
                                    {view === 'login' ? 'Anmelden' : 'Konto anlegen'}
                                </h2>
                                <p className="text-sm text-graphite mt-1.5 mb-7">
                                    {view === 'login' ? (
                                        <>Noch kein Konto? <button type="button" onClick={() => { setView('register'); setPassword(''); setConfirmPassword(''); }} className="text-ink font-medium underline underline-offset-2">Jetzt registrieren</button></>
                                    ) : (
                                        <>Schon registriert? <button type="button" onClick={() => { setView('login'); setPassword(''); setConfirmPassword(''); }} className="text-ink font-medium underline underline-offset-2">Zur Anmeldung</button></>
                                    )}
                                </p>
                                <form onSubmit={view === 'login' ? handleLoginSubmit : handleRegisterSubmit} className="space-y-4">
                                    <InputField
                                        id="email"
                                        label="E-Mail-Adresse"
                                        type="email"
                                        value={email}
                                        onChange={setEmail}
                                        placeholder="name@beispiel.de"
                                        autoComplete="email"
                                        autoFocus
                                    />
                                    <div>
                                        <InputField
                                            id="password"
                                            label="Passwort"
                                            type="password"
                                            value={password}
                                            onChange={setPassword}
                                            autoComplete={view === 'login' ? 'current-password' : 'new-password'}
                                        />
                                        {view === 'login' && (
                                            <button type="button" onClick={() => setView('forgot-password')} className="mt-2 text-xs text-graphite hover:text-ink underline-offset-2 hover:underline">
                                                Passwort vergessen?
                                            </button>
                                        )}
                                    </div>
                                    {view === 'register' && (
                                        <InputField
                                            id="confirm-password"
                                            label="Passwort wiederholen"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={setConfirmPassword}
                                            autoComplete="new-password"
                                        />
                                    )}
                                    <div className="pt-2">
                                        <SubmitButton
                                            text={view === 'login' ? 'Anmelden' : 'Konto anlegen'}
                                            loadingText={view === 'login' ? 'Wird angemeldet …' : 'Wird angelegt …'}
                                            isLoading={isLoading}
                                        />
                                    </div>
                                </form>
                            </>
                        ) : (
                            renderContent()
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Auth;
